import { translate } from '../i18n'

/**
 * Per-game config (blinds, chips, turn timer) for a schedule lives in different places depending on
 * the schedule table:
 *   - Recurring schedules (community + recurring lounge): in `tableConfigs[0]`.
 *   - One-off schedules (lounge one-off + private): as top-level scalars.
 *
 * This reader prefers `tableConfigs[0]`, then falls back to the top-level scalars, so callers can
 * read config uniformly across any schedule variant returned by `getSchedule`.
 */
export type ScheduleGameConfig = {
  blindTimer?: number
  startingChips?: number
  turnDuration?: number
}

export function readScheduleGameConfig(schedule: unknown): ScheduleGameConfig {
  if (!schedule || typeof schedule !== 'object') return {}
  const tc = (schedule as { tableConfigs?: Array<ScheduleGameConfig> }).tableConfigs?.[0]
  const top = schedule as ScheduleGameConfig
  return {
    blindTimer: tc?.blindTimer ?? top.blindTimer,
    startingChips: tc?.startingChips ?? top.startingChips,
    turnDuration: tc?.turnDuration ?? top.turnDuration
  }
}

/**
 * Display label for a schedule's blind increase interval.
 * A blind timer of 0 (or an unset one) means blinds never increase, so it reads as "No blind
 * increase" rather than "0 min blinds".
 */
export function formatBlindIncrease(blindTimerMin?: number): string {
  if (!blindTimerMin) return translate('scheduledGameCard.noBlindIncrease')
  return translate('scheduledGameCard.blindIncreaseEvery', { minutes: blindTimerMin })
}

/** Minimal shape the schedule day/time readers need; works for recurring and legacy schedule docs. */
type ScheduleDayTime = {
  repeatDaysOfWeekUTC?: number[]
  startDayOfWeekUTC?: number
  scheduledStartTime: number
}

/** Day-of-week (0-6) a schedule recurs on: repeatDaysOfWeekUTC[0], falling back to legacy startDayOfWeekUTC. */
export function scheduleDayOfWeekUTC(schedule: ScheduleDayTime): number | undefined {
  if (schedule.repeatDaysOfWeekUTC && schedule.repeatDaysOfWeekUTC.length > 0) {
    return schedule.repeatDaysOfWeekUTC[0]
  }
  return schedule.startDayOfWeekUTC
}

/** UTC time-of-day (hour/minute) a schedule starts, derived from scheduledStartTime. */
export function scheduleTimeOfDayUTC(schedule: ScheduleDayTime): { hours: number; minutes: number } {
  const d = new Date(schedule.scheduledStartTime)
  return { hours: d.getUTCHours(), minutes: d.getUTCMinutes() }
}

/** Build the next occurrence (>= now) for a weekly day-of-week at the given UTC time-of-day. */
export function nextOccurrenceFromDayTime(dayOfWeekUTC: number, hoursUTC: number, minutesUTC: number): number {
  const now = new Date()
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const daysUntilNext = (dayOfWeekUTC - today.getUTCDay() + 7) % 7
  const nextDate = new Date(today)
  nextDate.setUTCDate(today.getUTCDate() + daysUntilNext)
  nextDate.setUTCHours(hoursUTC, minutesUTC, 0, 0)
  if (nextDate.getTime() <= now.getTime()) {
    nextDate.setUTCDate(nextDate.getUTCDate() + 7)
  }
  return nextDate.getTime()
}
